import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Camera } from "lucide-react";
import Cookies from "js-cookie";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { deriveTypeInfo } from "../Appointments/TabContent/Appointment-View/utils";
import {
  checkInTrailer,
  checkInWalkUp,
  fetchDocksForWarehouse,
  fetchLocationChildren,
  fetchTrailersGrid,
  fetchYardSettings,
  fetchTrailerLocations,
  fetchYmsCapabilities,
  resolveWarehouseContextForCheckIn,
  resolveYmsGridOfficeCode,
  ymsHasSupervisorOverridePost,
} from "./api";
import {
  extractTrailerLocationSlots,
  filterLocationSlotsByOfficeCode,
  filterLocationSlotsByWarehouse,
  matchSlotForYardSelection,
  resolveCheckInLocationPayload,
} from "./yardLocationsUtils";
import { isYardSelectionUnavailable } from "./yardViewUtils";
import { CarrierTypeahead } from "./CarrierTypeahead";
import { FacilityTypeahead } from "./FacilityTypeahead";
import {
  buildTrailerGridRowFromCheckIn,
  recordPendingCheckInTrailerRow,
  isOutboundShipment,
  getPendingCheckInRows,
  normalizeTrailerNumberForComparison,
} from "./yardCheckInTrailerGrid";
import {
  appointmentTitleId,
  looksLikeWarehouseRefUuid,
  resolveAppointmentDockId,
  resolveCarrierNameFromAppointment,
  resolveOfficeCodeFromAppointment,
  resolveOfficeCodeFromLocation,
  resolveShipmentNumberFromAppointment,
  resolveWarehouseLocationRefForDocks,
  resolveWarehouseLocationRefForNameLookup,
  resolveWarehouseNameForTrailer,
  isManualCheckInDraft,
} from "./checkInGateUtils";
import { fetchLocationByRef } from "../Appointments/TabContent/api/AppointmentsApi";
import { fetchLocationsByOfficeCodes } from "../Manage-Team/api";
import { reconstructLocationList } from "../../../utils/locationHierarchy";
import { DisplayDetail } from "../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import {
  labelClass,
  baseInput,
  sectionCardClass,
  sectionHeaderBarClass,
  sectionFieldGridClass,
  sectionContentClass,
  gridSelectClass,
  toolbarPrimaryButtonClass,
  toolbarSecondaryButtonClass,
  ErrorHint,
  FormField,
} from "./yardRecordSectionStyles";

const DL_NUMBER_PATTERN = /^[a-zA-Z0-9]{6,20}$/;

// Equipment condition at gate arrival — important to capture before loading begins.
const EQUIPMENT_CONDITION_OPTIONS = ["Empty", "Partially Empty", "Partially Loaded", "Loaded"];

// The only two load types the yard recognises. These exact strings are what deriveTypeInfo
// returns as its label, so the seeded value always matches one of the options.
const LOAD_TYPE_OPTIONS = ["Live Load", "Drop Trailer"];

/** Carrier block with nothing picked — see carrierFields below. */
const EMPTY_CARRIER_FIELDS = {
  officeCode: "", addressLine1: "", city: "", state: "", zipCode: "",
};
const EQUIPMENT_CONDITION_TO_API = {
  Empty: "EMPTY",
  "Partially Empty": "PARTIALLY_EMPTY",
  "Partially Loaded": "PARTIALLY_LOADED",
  Loaded: "LOADED",
};

/** Backend @Digits(integer=10, fraction=3) for check-in weight. */
function isValidYmsCheckInWeight(s) {
  if (s == null || !String(s).trim()) return true;
  const t = String(s).trim();
  if (!/^\d+(\.\d+)?$/.test(t)) return false;
  const [intPart, frac = ""] = t.split(".");
  if (intPart.length < 1 || intPart.length > 10) return false;
  if (frac.length > 3) return false;
  return true;
}

async function loadCheckedInTrailerSet() {
  const set = new Set();
  try {
    const data = await fetchTrailersGrid();
    const api = Array.isArray(data) ? data : [];
    api.forEach((t) => {
      if (String(t?.gateStatus ?? "").toUpperCase() === "CHECKED_IN") {
        const n = normalizeTrailerNumberForComparison(t.trailerNumber);
        if (n) set.add(n);
      }
    });
  } catch {
    /* uniqueness check best-effort */
  }
  getPendingCheckInRows().forEach((row) => {
    if (String(row?.gateStatus ?? "").toUpperCase() === "CHECKED_IN") {
      const n = normalizeTrailerNumberForComparison(row.trailerNumber);
      if (n) set.add(n);
    }
  });
  return set;
}

/**
 * Full check-in form (wireframe: two columns, scan DL, complete).
 */
export function AppointmentCheckInFormModal({ appointment, onClose, onSubmitted }) {
  const ac = appointment?.appointmentCarrier ?? {};
  const carrierName =
    appointment?.carrierName ?? ac.carrierName ?? appointment?.carrier?.name ?? "";
  const initialTrailer =
    appointment?.trailerNumber ?? ac.trailerNumber ?? "";
  const initialDriver = ac.driverName ?? appointment?.driverName ?? "";

  // deriveTypeInfo(shipmentData, appointmentData) — this screen only ever has the gate-details
  // "appointment" shape (no shipmentStops array), so it belongs in the SECOND slot; passing it
  // as shipmentData (the bug here previously) meant getPickupStopReason always found nothing and
  // Load Type silently defaulted to "Live Load" regardless of the shipment's real stop reason.
  const loadTypeLabel = useMemo(
    () => deriveTypeInfo(null, appointment).label,
    [appointment]
  );

  const [shipmentNo, setShipmentNo] = useState(
    () => appointment?.shipmentNumber ?? ""
  );
  const [trailerNo, setTrailerNo] = useState(() => initialTrailer);
  const [carrier, setCarrier] = useState(() => carrierName);
  const [driverName, setDriverName] = useState(() => initialDriver);
  const [driverDlNumber, setDriverDlNumber] = useState("");
  const [driverLicenseState, setDriverLicenseState] = useState("");
  const [driverLicenseExpiry, setDriverLicenseExpiry] = useState("");
  const [driverEmail, setDriverEmail] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  // Left blank on a walk-up so the guard has to choose. deriveTypeInfo falls back to "Live Load"
  // whenever it has nothing to go on, and a walk-up gives it nothing — that default is a guess,
  // not a reading, and it is a costly one to get wrong: the backend rejects Hostler jobs for any
  // trailer that is not exactly DROP_TRAILER, so a wrongly-defaulted Live Load leaves the trailer
  // unmovable by a hostler with nothing on screen to explain why.
  const [loadType, setLoadType] = useState(() =>
    isManualCheckInDraft(appointment) ? "" : loadTypeLabel
  );
  const [gateShipmentType, setGateShipmentType] = useState(() =>
    isOutboundShipment(appointment) ? "Outbound" : "Inbound"
  );
  // TODO: hardcoded to match ShipmentDetailsView.js's own "53 Feet Dry Van" placeholder —
  // neither screen has real equipment-type data yet (Shipment.equipmentType is an unresolved
  // Integer code with no backing lookup table anywhere in the codebase). Replace both once a
  // real equipment-type source exists.
  const [equipmentType, setEquipmentType] = useState("53 Feet Dry Van");
  const [licensePlate, setLicensePlate] = useState("");
  const [licenseState, setLicenseState] = useState("");
  const [equipmentCondition, setEquipmentCondition] = useState("");
  const [sealNo, setSealNo] = useState("");
  const [weight, setWeight] = useState("");
  const [sectionName, setSectionName] = useState("");
  const [bayName, setBayName] = useState("");
  const [dockName, setDockName] = useState("");
  const [warehouseName, setWarehouseName] = useState(() =>
    resolveWarehouseNameForTrailer(appointment)
  );
  const [dlFile, setDlFile] = useState(null);
  const [sealImageFile, setSealImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [trailerLocationSlots, setTrailerLocationSlots] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState("");

  /**
   * Create-mode only (LGX-1104): the Shipment these feed does not exist yet, so unlike every
   * other field in Core Shipment Details there is no appointment to read them from. Grouped in
   * two objects rather than a dozen useState calls — they are only ever read as a pair when the
   * walk-up payload is assembled.
   */
  // Seeded from the matched shipment's BOL reference when it has one; blank on a walk-up, where
  // the guard is the first person to record it.
  const [bolNumber, setBolNumber] = useState(() => String(appointment?.bolNumber ?? ""));
  const [soNumber, setSoNumber] = useState(() => String(appointment?.soNumber ?? ""));
  const [poNumber, setPoNumber] = useState(() => String(appointment?.poNumber ?? ""));
  const [originFields, setOriginFields] = useState({
    name: "", addressLine1: "", addressLine2: "", city: "", state: "", zipCode: "", country: "",
  });
  const [destinationFields, setDestinationFields] = useState({
    name: "", addressLine1: "", addressLine2: "", city: "", state: "", zipCode: "", country: "",
  });
  const setOriginField = useCallback(
    (key, value) => setOriginFields((prev) => ({ ...prev, [key]: value })), []);

  /**
   * The Carrier block that sits beside Carrier Name. Seeded from the appointment so a matched
   * check-in renders exactly what it always did, then replaced wholesale when the guard picks a
   * carrier from the type-ahead.
   *
   * Read-only throughout: unlike Origin/Destination, there is nowhere for a hand-typed carrier
   * address to go. The Shipment's carrier party is built from a carrier organization, so an
   * address typed against a carrier we have no record of would be discarded on submit — better
   * to show the field empty than to accept input the check-in cannot keep.
   */
  const [carrierFields, setCarrierFields] = useState(() => ({
    officeCode: String(appointment?.carrierOfficeCode ?? ""),
    addressLine1: String(appointment?.carrierAddressLine1 ?? ""),
    city: String(appointment?.carrierCity ?? ""),
    state: String(appointment?.carrierState ?? ""),
    zipCode: String(appointment?.carrierZip ?? ""),
  }));
  const applyPickedCarrier = useCallback((picked) => {
    const text = (v) => String(v ?? "").trim();
    setCarrierFields({
      officeCode: text(picked?.officeCode),
      addressLine1: text(picked?.addressLine1),
      city: text(picked?.city),
      state: text(picked?.state),
      zipCode: text(picked?.zipCode),
    });
  }, []);
  const setDestinationField = useCallback(
    (key, value) => setDestinationFields((prev) => ({ ...prev, [key]: value })), []);

  /**
   * One picked facility fills the whole block. The address lines are overwritten even when
   * blank on the Location, so a pick always leaves the form describing exactly one place -
   * half-replacing them is how you get a new facility's name sitting on the previous one's
   * city and zip, which is worse than either on its own.
   */
  const applyPickedFacility = useCallback((setFields) => (loc) => {
    const text = (v) => String(v ?? "").trim();
    setFields((prev) => ({
      ...prev,
      name: text(loc?.locationName),
      addressLine1: text(loc?.addressLine1),
      addressLine2: text(loc?.addressLine2),
      city: text(loc?.city),
      state: text(loc?.state),
      zipCode: text(loc?.zipCode),
      country: text(loc?.country),
    }));
  }, []);
  const applyOriginFacility = useMemo(
    () => applyPickedFacility(setOriginFields), [applyPickedFacility]);
  const applyDestinationFacility = useMemo(
    () => applyPickedFacility(setDestinationFields), [applyPickedFacility]);

  /** Plant/facility options for the walk-up "Location" dropdown — see the effect below. */
  const [plantOptions, setPlantOptions] = useState([]);
  const [plantOptionsLoading, setPlantOptionsLoading] = useState(false);

  const [sealVerified, setSealVerified] = useState(true);
  const [sealDiscrepancyNotes, setSealDiscrepancyNotes] = useState("");
  const [supervisorBypassReason, setSupervisorBypassReason] = useState("");
  const [lookupSkipped, setLookupSkipped] = useState(false);
  const [ymsCapabilities, setYmsCapabilities] = useState(null);

  const [yardSections, setYardSections] = useState([]);
  const [yardLoading, setYardLoading] = useState(false);
  const [yardLoadError, setYardLoadError] = useState("");
  const [warehouseDocks, setWarehouseDocks] = useState([]);
  const [warehouseDocksLoading, setWarehouseDocksLoading] = useState(false);
  // Location (the plant/facility, e.g. "Diamond Pet Foods - Dumas") and Warehouse (a child of
  // that plant, in the Appointment module's own hierarchy — each warehouse has its own docks,
  // independent of YMS's Section/Parking Bay hierarchy which hangs off the plant directly) are
  // two different things. plantLocationId is the resolved plant's numeric id, used to fetch its
  // warehouse children for the Dock path specifically.
  const [plantLocationId, setPlantLocationId] = useState(null);
  /** Display name of the resolved PLANT (e.g. "Diamond Pet Foods - Lathrop"), for the "Location"
   * field below -- kept separate from warehouseName, which is the raw appointment warehouse
   * value and can itself already name a specific child warehouse (e.g. "Roth Warehouse"),
   * not the plant. Falls back to warehouseName until resolution completes so the field never
   * flashes blank. */
  const [plantLocationName, setPlantLocationName] = useState("");
  const [dockWarehouseOptions, setDockWarehouseOptions] = useState([]);
  const [dockWarehouseLoading, setDockWarehouseLoading] = useState(false);
  const [dockWarehouseName, setDockWarehouseName] = useState("");
  /** Set when the appointment's own warehouse name/ref resolved directly to a specific
   * WAREHOUSE-typed location (e.g. "Roth Warehouse") rather than its parent plant -- used to
   * auto-select that warehouse in the dropdown below once its sibling options load, instead of
   * leaving the field on "—" with nothing pre-picked even though the appointment already knows. */
  const [resolvedAppointmentWarehouseRef, setResolvedAppointmentWarehouseRef] = useState("");
  /** locationRef of the selected dockWarehouseOptions entry — used (in preference to the name)
   * to fetch docks, since a warehouse can share its display name with its parent plant (e.g.
   * Ripon, Dumas, Rushville all name the single warehouse identically to the plant), which
   * makes name-based dock lookup on the backend ambiguous and can silently resolve to the
   * plant instead of the warehouse, returning zero docks. */
  const [dockWarehouseRef, setDockWarehouseRef] = useState("");
  /** Resolved warehouse locationRef for GET /api/dock/docks/{ref} (office-scoped warehouse). */
  const [warehouseLocationRef, setWarehouseLocationRef] = useState(() =>
    resolveWarehouseLocationRefForDocks(appointment, resolveWarehouseNameForTrailer(appointment))
  );
  /** Resolved office code for the warehouse selected in this form (not just the active grid office). */
  const [warehouseOfficeCode, setWarehouseOfficeCode] = useState(() =>
    resolveOfficeCodeFromAppointment(appointment) || resolveYmsGridOfficeCode()
  );
  /** Increment on warehouse blur so layout refetches even if the typed value did not change (e.g. office code now available). */
  const [yardLayoutFetchNonce, setYardLayoutFetchNonce] = useState(0);

  const [checkedInTrailers, setCheckedInTrailers] = useState(() => new Set());

  const [touched, setTouched] = useState({});

  const dlInputRef = useRef(null);
  const sealImageInputRef = useRef(null);
  /** Prepopulate dock from appointment once per open; yard layout reload clears fields first. */
  const appointmentDockPrepopulatedRef = useRef(false);
  /** Prepopulate dock-path warehouse from appointment once per open, same reasoning as above. */
  const appointmentWarehousePrepopulatedRef = useRef(false);

  /**
   * Set once the guard picks a plant in the walk-up "Location" dropdown, after which that pick
   * is authoritative and no async resolution may overwrite it. See the warehouse-context effect
   * below for what this is defending against.
   */
  const plantPickedByGuardRef = useRef(false);

  /** Office code scoped to the selected warehouse — drives yard/locations/dock APIs and check-in save. */
  const officeCodeForApi = useMemo(
    () =>
      String(
        warehouseOfficeCode ||
          resolveOfficeCodeFromAppointment(appointment) ||
          resolveYmsGridOfficeCode() ||
          ""
      ).trim(),
    [warehouseOfficeCode, appointment]
  );

  /** Latest values for open-modal prefetch (runs after layout so warehouse sync is visible). */
  const warehouseNameRef = useRef(warehouseName);
  const officeCodeRef = useRef(officeCodeForApi);
  warehouseNameRef.current = warehouseName;
  officeCodeRef.current = officeCodeForApi;

  const titleId = appointmentTitleId(appointment);
  /**
   * LGX-1101 — walk-up freight: the guard searched, nothing matched, and this form opened blank
   * so the details can be entered by hand. There is no appointment record behind it, so the
   * title, the appointment-id submit guard and the payload all have to account for that.
   */
  const manualEntry = isManualCheckInDraft(appointment);
  const manualSearchTerm = String(appointment?.manualSearchTerm ?? "").trim();
  const cancelled = !!appointment?.cancelled;

  const outbound = String(gateShipmentType).trim().toLowerCase().startsWith("out");

  const touch = useCallback((field) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  }, []);

  /**
   * Typing past a pick unbinds it. The office code and address describe the carrier that was
   * chosen, and carrying them onto a name the guard has since retyped would attach the wrong
   * carrier to the shipment. The type-ahead fires onChange before onPick, so choosing a real
   * suggestion clears and re-fills in the same interaction.
   */
  const handleCarrierNameChange = useCallback((v) => {
    setCarrier(v);
    touch("carrier");
    setCarrierFields(EMPTY_CARRIER_FIELDS);
  }, [touch]);

  const appointmentWarehouseSeed = resolveWarehouseNameForTrailer(appointment);

  useLayoutEffect(() => {
    setWarehouseName(appointmentWarehouseSeed);
  }, [appointmentWarehouseSeed]);

  /** Resolve warehouse locationRef + office code from the warehouse name on this form. */
  useEffect(() => {
    const wh = warehouseName.trim();
    if (!wh) {
      setWarehouseOfficeCode("");
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const ctx = await resolveWarehouseContextForCheckIn({
          warehouseName: wh,
          warehouseLocationRef,
          appointment,
        });
        if (cancelled) return;
        if (ctx.locationRef) setWarehouseLocationRef(ctx.locationRef);
        // A walk-up plant comes from the guard picking it in the "Location" dropdown - it is
        // not derived from anything, and must not be re-derived. This effect re-runs on that
        // pick (it changes warehouseName), and name-based resolution can land on a DIFFERENT
        // Location row than the one picked: a WAREHOUSE child sharing its plant name, or, when
        // nothing matches, null. Either wrote an id back into plantLocationId that is not in the
        // POINT-filtered option list, so the select fell back to value="" and the pick looked
        // like it never registered - the guard had to repeat it until the surrounding refs had
        // filled in enough for resolution to agree with the pick.
        if (!plantPickedByGuardRef.current) {
          setPlantLocationId(ctx.location?.id ?? null);
          setPlantLocationName(ctx.location?.locationName || "");
        }
        setResolvedAppointmentWarehouseRef(ctx.resolvedWarehouse?.locationRef || "");
        const oc =
          ctx.officeCode ||
          resolveOfficeCodeFromAppointment(appointment) ||
          resolveYmsGridOfficeCode();
        setWarehouseOfficeCode(String(oc).trim());
      } catch {
        if (!cancelled) {
          setWarehouseOfficeCode(
            resolveOfficeCodeFromAppointment(appointment) ||
              resolveYmsGridOfficeCode()
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [warehouseName, warehouseLocationRef, appointment, yardLayoutFetchNonce]);

  /**
   * Mirror the warehouse-field blur refresh: bump nonce once when this check-in modal opens for an appointment,
   * so yard layout + bay/dock APIs run without requiring the user to tab out of Warehouse name.
   */
  useEffect(() => {
    if (!appointment?.appointmentId) return undefined;
    appointmentDockPrepopulatedRef.current = false;
    appointmentWarehousePrepopulatedRef.current = false;
    setWarehouseLocationRef(
      resolveWarehouseLocationRefForDocks(appointment, resolveWarehouseNameForTrailer(appointment))
    );
    setWarehouseOfficeCode(
      resolveOfficeCodeFromAppointment(appointment) || resolveYmsGridOfficeCode()
    );
    const id = requestAnimationFrame(() => {
      const wh = warehouseNameRef.current.trim();
      const oc = officeCodeRef.current.trim();
      if (wh && oc) setYardLayoutFetchNonce((n) => n + 1);
    });
    return () => cancelAnimationFrame(id);
  }, [appointment?.appointmentId]);

  /** Gate payloads often send warehouse as locationRef UUID; resolve display name via locations API. */
  useEffect(() => {
    if (!appointment?.appointmentId) return undefined;
    let cancelled = false;
    (async () => {
      if (resolveWarehouseNameForTrailer(appointment)) return;
      const ref = resolveWarehouseLocationRefForNameLookup(appointment);
      if (!ref) return;
      try {
        const loc = await fetchLocationByRef(ref);
        const name = loc?.locationName != null ? String(loc.locationName).trim() : "";
        if (cancelled || !name) return;
        setWarehouseName(name);
        if (looksLikeWarehouseRefUuid(ref)) setWarehouseLocationRef(ref);
        const oc = resolveOfficeCodeFromLocation(loc);
        if (oc) setWarehouseOfficeCode(oc);
      } catch {
        /* keep field empty — user can type name */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointment?.appointmentId]);

  const canSkipGateLookup = ymsHasSupervisorOverridePost(ymsCapabilities);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const caps = await fetchYmsCapabilities();
        if (!cancelled) setYmsCapabilities(caps);
      } catch {
        if (!cancelled) setYmsCapabilities(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canSkipGateLookup) setLookupSkipped(false);
  }, [canSkipGateLookup]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await loadCheckedInTrailerSet();
      if (!cancelled) setCheckedInTrailers(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [appointment?.appointmentId]);

  useEffect(() => {
    const wh = warehouseName.trim();
    if (!wh) {
      setYardSections([]);
      setYardLoadError("");
      setSectionName("");
      setBayName("");
      setDockName("");
      return;
    }
    let cancelled = false;
    setYardLoading(true);
    setYardLoadError("");
    (async () => {
      try {
        const data = await fetchYardSettings(
          wh,
          officeCodeForApi ? [officeCodeForApi] : undefined
        );
        if (cancelled) return;
        const sections = Array.isArray(data?.sections) ? data.sections : [];
        setYardSections(sections);
        setSectionName("");
        setBayName("");
        setDockName("");
      } catch {
        if (!cancelled) {
          setYardSections([]);
          setSectionName("");
          setBayName("");
          setDockName("");
          setYardLoadError(
            "Yard layout could not be loaded. Confirm warehouse name matches yard settings."
          );
        }
      } finally {
        if (!cancelled) setYardLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [warehouseName, officeCodeForApi, yardLayoutFetchNonce]);

  // Plant/facility list for the walk-up "Location" dropdown. A walk-up has no appointment to
  // seed the field from, so it opened blank as free text and the browser's own autofill was the
  // only thing offering to fill it in. Same source and filtering as the module's other facility
  // pickers (yardmanagement.js, MyAppointments.js): reconstructLocationList first repairs entries
  // the backend's @JsonIdentityInfo collapsed to a bare id, then only top-level POINT rows belong
  // here — WAREHOUSE rows are children offered by the "Warehouse" dropdowns beside this field,
  // and GEOGRAPHY rows are internal rate-shopping graph nodes, never user-facing.
  useEffect(() => {
    if (!manualEntry) {
      setPlantOptions([]);
      return undefined;
    }
    let cancelled = false;
    setPlantOptionsLoading(true);
    (async () => {
      try {
        const res = await fetchLocationsByOfficeCodes();
        if (cancelled) return;
        const all = Array.isArray(res?.data) ? res.data : [];
        setPlantOptions(
          reconstructLocationList(all)
            .filter((loc) => String(loc?.locationType ?? "").toUpperCase() === "POINT")
            .map((loc) => ({
              id: loc.id,
              name: String(loc.locationName ?? "").trim(),
              ref: loc.locationRef ?? "",
            }))
            .filter((loc) => loc.name)
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch {
        // Non-fatal: the field keeps its free-text fallback, so a walk-up is never blocked here.
        if (!cancelled) setPlantOptions([]);
      } finally {
        if (!cancelled) setPlantOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [manualEntry]);

  // Warehouse children of the resolved plant — populates the "Warehouse" dropdown that gates
  // the Dock path. Independent of Section/Parking Bay, which hang off the plant directly.
  useEffect(() => {
    if (!plantLocationId) {
      setDockWarehouseOptions([]);
      return undefined;
    }
    let cancelled = false;
    setDockWarehouseLoading(true);
    (async () => {
      try {
        const children = await fetchLocationChildren(plantLocationId);
        if (cancelled) return;
        const options = children
          .filter((loc) => String(loc?.locationType ?? "").toUpperCase() === "WAREHOUSE")
          .map((loc) => ({
            id: loc.id,
            name: String(loc.locationName ?? "").trim(),
            ref: loc.locationRef ?? loc.ref ?? "",
          }))
          .filter((wh) => wh.name);
        setDockWarehouseOptions(options);

        // Walk-ups are exempt: this pre-selects the DOCK-path warehouse, and doing so sets
        // usesDockPath, which disables Section and Parking Bay. That is right when an
        // appointment named the warehouse — it already chose the dock path. On a walk-up the ref
        // comes from the guard's own Warehouse pick in Row 1 (resolveWarehouseContextForCheckIn
        // returns the warehouse as resolvedWarehouse while climbing to its parent plant), so
        // firing here would silently force the dock path the moment a warehouse is chosen and
        // lock the guard out of the bay path entirely. Let them pick the path instead.
        if (!manualEntry && !appointmentWarehousePrepopulatedRef.current && resolvedAppointmentWarehouseRef) {
          const matched = options.find((wh) => wh.ref === resolvedAppointmentWarehouseRef);
          if (matched) {
            appointmentWarehousePrepopulatedRef.current = true;
            setSectionName("");
            setBayName("");
            setDockWarehouseName(matched.name);
            setDockWarehouseRef(matched.ref);
          }
        }
      } catch {
        if (!cancelled) setDockWarehouseOptions([]);
      } finally {
        if (!cancelled) setDockWarehouseLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plantLocationId, resolvedAppointmentWarehouseRef, manualEntry]);

  // Escape hatch for a walk-up whose picked plant has no WAREHOUSE children: the Warehouse
  // dropdowns would stay empty, leaving warehouseName unsettable and the check-in unfinishable.
  // The free-text field this dropdown replaced let the guard type anything, so fall back to the
  // plant's own name as the yard-settings key — which is the right key for an org that
  // configured its yard at facility level rather than per warehouse.
  useEffect(() => {
    if (!manualEntry || !plantLocationId) return;
    if (dockWarehouseLoading || dockWarehouseOptions.length > 0) return;
    if (warehouseName.trim()) return;
    setWarehouseName(plantLocationName);
  }, [
    manualEntry,
    plantLocationId,
    plantLocationName,
    dockWarehouseLoading,
    dockWarehouseOptions.length,
    warehouseName,
  ]);

  // Docks are scoped to the SELECTED warehouse (cascading), not the plant — a plant can have
  // multiple warehouses, each with its own independent set of docks.
  useEffect(() => {
    // Prefer the warehouse's own locationRef (unique) over its display name — a warehouse can
    // share its name with its parent plant (Ripon, Dumas, Rushville all do), and the backend's
    // name-based dock lookup silently picks whichever location matches first, which may be the
    // plant (zero docks) instead of the warehouse.
    const dockFetchKey = (dockWarehouseRef || dockWarehouseName).trim();
    if (!dockFetchKey) {
      setWarehouseDocks([]);
      setWarehouseDocksLoading(false);
      return undefined;
    }
    let cancelled = false;
    setWarehouseDocksLoading(true);
    (async () => {
      try {
        const data = await fetchDocksForWarehouse(dockFetchKey);
        if (!cancelled) {
          setWarehouseDocks(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) setWarehouseDocks([]);
      } finally {
        if (!cancelled) setWarehouseDocksLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dockWarehouseName, dockWarehouseRef]);

  useEffect(() => {
    const wh = warehouseName.trim();
    if (!wh || !officeCodeForApi) {
      setTrailerLocationSlots([]);
      setLocationsError("");
      setLocationsLoading(false);
      return;
    }
    let cancelled = false;
    setLocationsLoading(true);
    setLocationsError("");
    (async () => {
      try {
        const data = await fetchTrailerLocations(
          officeCodeForApi ? [officeCodeForApi] : undefined
        );
        if (cancelled) return;
        setTrailerLocationSlots(
          filterLocationSlotsByOfficeCode(
            extractTrailerLocationSlots(data),
            officeCodeForApi,
            { strict: false }
          )
        );
      } catch (err) {
        if (!cancelled) {
          setTrailerLocationSlots([]);
          const serverMsg =
            err?.response?.data?.message ||
            err?.response?.data?.content ||
            err?.message;
          setLocationsError(
            serverMsg
              ? String(serverMsg)
              : "Could not load yard locations API; bay/dock IDs may be unavailable."
          );
        }
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [warehouseName, officeCodeForApi, yardLayoutFetchNonce]);

  const warehouseLocationSlots = useMemo(
    () =>
      // Section/Parking Bay hang off the PLANT directly (independent of any specific child
      // warehouse), so this scopes by the plant's own ref, not a selected warehouse's.
      filterLocationSlotsByWarehouse(
        trailerLocationSlots,
        warehouseName,
        warehouseLocationRef
      ),
    [trailerLocationSlots, warehouseName, warehouseLocationRef]
  );

  // Docks in yms_yard_dock are scoped by the CHILD warehouse's own ref/name (e.g. "Diamond -
  // Lathrop - Harlan Warehouse"), not the plant's — so dock id resolution needs a separate,
  // warehouse-scoped slice of the same already-fetched /api/trailers/locations data.
  const dockLocationSlots = useMemo(
    () =>
      dockWarehouseName
        ? filterLocationSlotsByWarehouse(trailerLocationSlots, dockWarehouseName, dockWarehouseRef)
        : [],
    [trailerLocationSlots, dockWarehouseName, dockWarehouseRef]
  );

  const selectedSection = useMemo(
    () => yardSections.find((s) => String(s?.name ?? "") === sectionName) ?? null,
    [yardSections, sectionName]
  );

  const bayOptions = (selectedSection?.bayNames ?? []).filter(
    (b) =>
      !isYardSelectionUnavailable(warehouseLocationSlots, {
        sectionName,
        bayName: b,
        dockName: "",
      })
  );
  const warehouseDockOptions = useMemo(
    () =>
      warehouseDocks
        .map((dock) => ({
          id: dock.dockId ?? dock.id,
          name: String(dock.dockName ?? dock.name ?? "").trim(),
        }))
        .filter((dock) => dock.name)
        .filter(
          (dock) =>
            !isYardSelectionUnavailable(dockLocationSlots, {
              sectionName: "",
              bayName: "",
              dockName: dock.name,
            })
        ),
    [warehouseDocks, dockLocationSlots]
  );
  const hasBayLayout = useMemo(
    () =>
      yardSections.some((section) =>
        (section.bayNames ?? []).some(
          (bay) =>
            !isYardSelectionUnavailable(warehouseLocationSlots, {
              sectionName: section.name,
              bayName: bay,
              dockName: "",
            })
        )
      ),
    [yardSections, warehouseLocationSlots]
  );
  const usesBayPath = Boolean(sectionName || bayName);
  // Picking a Warehouse (step 1 of the Dock path) already declares intent to use a dock, even
  // before a specific Dock is chosen — disables the bay row the same way picking a Section does.
  const usesDockPath = Boolean(dockWarehouseName || dockName);
  const needsBay = usesBayPath && bayOptions.length > 0;
  const needsDock = usesDockPath && warehouseDockOptions.length > 0;

  useEffect(() => {
    if (!selectedSection) return;
    if (bayName && !bayOptions.includes(bayName)) setBayName("");
  }, [selectedSection, bayOptions, bayName]);

  useEffect(() => {
    if (dockName && !warehouseDockOptions.some((dock) => dock.name === dockName)) {
      setDockName("");
    }
  }, [warehouseDockOptions, dockName]);

  /** When appointment carries a dock id, select matching warehouse dock after docks load. */
  useEffect(() => {
    if (appointmentDockPrepopulatedRef.current) return;
    if (warehouseDocksLoading) return;

    const apptDockId = resolveAppointmentDockId(appointment);
    if (apptDockId == null) return;

    const matched = warehouseDocks.find(
      (dock) => Number(dock.dockId ?? dock.id) === apptDockId
    );
    const dockLabel = String(matched?.dockName ?? matched?.name ?? "").trim();
    if (!dockLabel) return;

    const available = warehouseDockOptions.some((dock) => dock.name === dockLabel);
    if (!available) return;

    appointmentDockPrepopulatedRef.current = true;
    setSectionName("");
    setBayName("");
    setDockName(dockLabel);
  }, [
    appointment,
    warehouseDocks,
    warehouseDocksLoading,
    warehouseDockOptions,
  ]);

  const checkInLocationPayload = useMemo(() => {
    const isDockOnlyPath = Boolean(dockName) && !sectionName;

    if (isDockOnlyPath) {
      // Resolve the real yms_yard_dock.dock_id from GET /api/trailers/locations by NAME —
      // warehouseDocks (GET /api/dock/docks/{warehouseRef}) is a different table
      // (appointment.Docks) with its own unrelated id sequence; only its dock NAMES are
      // used to populate the dropdown, never its ids.
      const dockMatch = matchSlotForYardSelection(dockLocationSlots, { dockName });
      const numericDockId = dockMatch.currentDockId;
      if (numericDockId != null) {
        return {
          selectedSpotId: numericDockId,
          selectedSpotType: "DOCK",
          currentDockId: numericDockId,
          currentBayId: undefined,
          baySlot: null,
          dockSlot: dockMatch.slot ?? null,
        };
      }
      return {
        selectedSpotId: undefined,
        selectedSpotType: undefined,
        currentBayId: undefined,
        currentDockId: undefined,
        baySlot: null,
        dockSlot: null,
      };
    }

    return resolveCheckInLocationPayload(warehouseLocationSlots, {
      sectionName,
      bayName,
      dockName,
      sectionId: selectedSection?.sectionId ?? selectedSection?.section_id,
    });
  }, [
    warehouseLocationSlots,
    dockLocationSlots,
    sectionName,
    bayName,
    dockName,
    selectedSection,
  ]);

  const errors = useMemo(() => {
    const e = {};
    const tn = trailerNo.trim();
    if (!tn) e.trailerNo = "Trailer number is required.";
    else if (checkedInTrailers.has(normalizeTrailerNumberForComparison(tn))) {
      e.trailerNo = "This trailer is already checked in to the yard.";
    }

    if (!carrier.trim()) e.carrier = "Carrier is required.";
    if (!driverName.trim()) e.driverName = "Driver name is required.";

    const dln = driverDlNumber.trim();
    if (!dln) e.driverDlNumber = "Driver license number is required.";
    else if (!DL_NUMBER_PATTERN.test(dln)) {
      e.driverDlNumber = "Use 6–20 letters or numbers only.";
    }
    // Driver license image is optional — capture it when available (scan/upload), but don't
    // block check-in on it.

    if (!loadType.trim()) e.loadType = "Load type is required.";

    if (!equipmentCondition.trim()) {
      e.equipmentCondition = "Equipment condition is required.";
    }

    if (!warehouseName.trim()) e.warehouseName = "Warehouse name is required.";

    if (yardLoadError) e.yardLayout = yardLoadError;
    else if (!yardLoading && warehouseName.trim() && !hasBayLayout && !warehouseDockOptions.length) {
      e.yardLayout =
        "No parking bays or docks found for this warehouse. Configure yard settings or warehouse docks.";
    }

    const hasBayPath = Boolean(sectionName && bayName);
    const hasDockPath = Boolean(dockName);

    if (!hasBayPath && !hasDockPath) {
      e.yardLocation = "Select section and parking bay, or select a dock below.";
    }

    if (usesBayPath && !usesDockPath) {
      if (!sectionName) e.sectionName = "Select a section from the yard layout.";
      if (sectionName && needsBay && !bayName) {
        e.bayName = "Select a parking bay from the yard layout.";
      }
      if (sectionName && !needsBay && !bayOptions.length) {
        e.sectionLayout =
          "This section has no parking bays configured. Update yard settings or choose a dock.";
      }
    }

    if (usesDockPath && !usesBayPath) {
      if (!dockName) e.dockName = "Select a dock for this warehouse.";
      else if (!warehouseDockOptions.some((dock) => dock.name === dockName)) {
        e.dockName = "Selected dock is not available for this warehouse.";
      }
    }

    if (
      !locationsError &&
      !locationsLoading &&
      (warehouseLocationSlots.length > 0 || dockLocationSlots.length > 0) &&
      warehouseName.trim()
    ) {
      if (
        hasBayPath &&
        bayName &&
        checkInLocationPayload.currentBayId == null &&
        !checkInLocationPayload.baySlot
      ) {
        e.bayLocationId =
          "Could not resolve parking bay ID from yard locations. Refresh or pick another bay.";
      } else if (
        hasBayPath &&
        bayName &&
        checkInLocationPayload.baySlot &&
        checkInLocationPayload.currentBayId == null
      ) {
        e.bayLocationId =
          "Selected bay was found but has no ID in yard locations. Refresh or pick another bay.";
      }
      // No client-side dockLocationId check: unlike bays, a dock picked from the Appointment
      // module's dock list may have no yms_yard_dock counterpart yet — the backend resolves (and
      // creates, if needed) that occupancy row from the dockBay name sent at submit time.
    }

    if (!outbound) {
      if (!sealNo.trim()) e.sealNo = "Seal number is required for inbound shipments.";
    }

    const w = weight.trim();
    if (w && !isValidYmsCheckInWeight(w)) {
      e.weight = "Weight: up to 10 digits before the decimal and 3 after (e.g. 1234567890.123).";
    }

    if (locationsError) e.locationsApi = locationsError;

    return e;
  }, [
    trailerNo,
    checkedInTrailers,
    carrier,
    driverName,
    driverDlNumber,
    dlFile,
    loadType,
    equipmentCondition,
    warehouseName,
    yardLoadError,
    yardLoading,
    hasBayLayout,
    warehouseDockOptions,
    usesBayPath,
    usesDockPath,
    sectionName,
    selectedSection,
    needsBay,
    bayName,
    bayOptions.length,
    needsDock,
    dockName,
    outbound,
    sealNo,
    weight,
    locationsError,
    locationsLoading,
    warehouseLocationSlots.length,
    dockLocationSlots.length,
    checkInLocationPayload,
  ]);

  const show = useCallback(
    (field) => (touched[field] ? errors[field] : ""),
    [touched, errors]
  );

  const FIELD_ERROR_ORDER = [
    "trailerNo",
    "carrier",
    "driverName",
    "driverDlNumber",
    "loadType",
    "equipmentCondition",
    "warehouseName",
    "yardLayout",
    "yardLocation",
    "sectionName",
    "sectionLayout",
    "bayName",
    "bayLocationId",
    "dockName",
    "dockLocationId",
    "sealNo",
    "weight",
    "locationsApi",
  ];

  /** All reasons the form cannot be submitted yet (shown in modal footer). */
  const submitBlockers = useMemo(() => {
    const list = [];
    for (const k of FIELD_ERROR_ORDER) {
      if (errors[k]) list.push(errors[k]);
    }
    if (cancelled) list.push("This appointment is cancelled.");
    // A walk-up genuinely has no appointment (LGX-1102: the API accepts a missing reference
    // rather than inventing a placeholder), so a missing id is only a blocker when this form
    // was opened from a matched search.
    else if (!manualEntry && !appointment?.appointmentId) list.push("Appointment id is missing. Return to search and open the appointment again.");
    if (yardLoading) list.push("Yard layout is still loading—wait or confirm the warehouse name.");
    if (warehouseDocksLoading) list.push("Warehouse docks are still loading—wait a moment.");
    if (locationsLoading) list.push("Yard locations are still loading—wait a moment.");
    return list;
  }, [
    errors,
    cancelled,
    manualEntry,
    appointment?.appointmentId,
    yardLoading,
    warehouseDocksLoading,
    locationsLoading,
  ]);

  const submitBlockedHint = submitBlockers[0] || "";

  const formValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  const canComplete =
    formValid &&
    !cancelled &&
    !submitting &&
    !yardLoading &&
    !warehouseDocksLoading &&
    !locationsLoading &&
    (manualEntry || !!appointment?.appointmentId);

  const buildLocationLabel = useCallback(() => {
    const parts = [];
    if (sectionName) parts.push(`Section: ${sectionName}`);
    if (bayName) parts.push(`Bay: ${bayName}`);
    if (dockWarehouseName) parts.push(`Warehouse: ${dockWarehouseName}`);
    if (dockName) parts.push(`Dock: ${dockName}`);
    return parts.join(" · ");
  }, [sectionName, bayName, dockWarehouseName, dockName]);

  const handleWeightChange = (raw) => {
    let v = raw.replace(/[^\d.]/g, "");
    const dots = v.split(".");
    v = dots.length <= 1 ? v : `${dots[0]}.${dots.slice(1).join("").replace(/\./g, "")}`;
    const [intP, frac = ""] = v.split(".");
    const intTrim = intP.slice(0, 10);
    const fracTrim = frac.slice(0, 3);
    v = frac !== undefined && frac !== "" ? `${intTrim}.${fracTrim}`.replace(/\.$/, "") : intTrim;
    setWeight(v);
  };

  const handleComplete = async () => {
    if (!canComplete) return;
    const warehouseNameTrimmed = warehouseName.trim();

    setSubmitting(true);
    try {
      const shipmentDirection = outbound ? "OUTBOUND" : "INBOUND";

      const shipmentForApi =
        shipmentNo.trim() || resolveShipmentNumberFromAppointment(appointment);
      const carrierForApi =
        carrier.trim() || resolveCarrierNameFromAppointment(appointment);
      const locationForApi = buildLocationLabel();

      const performedByUserId = JSON.parse(Cookies.get("userData") || "{}")?.id;
      const {
        selectedSpotId,
        selectedSpotType,
        currentBayId: cb,
        currentDockId: cd,
      } = checkInLocationPayload;

      const officeCode = officeCodeForApi;
      if (!officeCode) {
        notify.error({
          title: "Check-in failed",
          message:
            "Office code could not be resolved for this warehouse. Confirm the warehouse name or log in again.",
        });
        setSubmitting(false);
        return;
      }

      const sectionIdRaw =
        selectedSection?.sectionId ??
        selectedSection?.section_id ??
        checkInLocationPayload.baySlot?.sectionId ??
        checkInLocationPayload.baySlot?.section_id;
      const sectionId =
        sectionIdRaw != null && sectionIdRaw !== ""
          ? Number(sectionIdRaw)
          : undefined;

      /** Matches backend TrailerCheckInRequest (POST /api/trailers/check-in). */
      const checkInPayload = {
        // Omitted for a walk-up (LGX-1102): the check-in endpoint accepts a missing appointment
        // reference and checks the trailer in against the shipment alone. No appointment record
        // is created for it — deliberately, so the appointment chain keeps no meaningless rows.
        appointmentId: manualEntry ? undefined : appointment.appointmentId,
        officeCode,
        warehouseRef: warehouseNameTrimmed || undefined,
        sectionId: Number.isFinite(sectionId) ? sectionId : undefined,
        sectionName: sectionName.trim() || undefined,
        trailerNumber: (trailerNo.trim() || appointment?.trailerNumber || "").trim(),
        shipmentNumber: shipmentForApi || undefined,
        sealNumber: sealNo.trim() || undefined,
        weight: weight.trim() || undefined,
        carrierName: carrierForApi || undefined,
        selectedSpotId,
        selectedSpotType,
        currentBayId: cb,
        currentDockId: cd,
        // Dock name from the Appointment module's own dock list — lets the backend create the
        // matching yms_yard_dock occupancy row on first use when the two catalogs haven't been
        // reconciled yet for this warehouse (see resolveSpotFromLabel).
        dockBay: usesDockPath ? dockName.trim() || undefined : undefined,
        driverName: driverName.trim() || undefined,
        driverLicenseNumber: driverDlNumber.trim(),
        driverLicenseState: driverLicenseState.trim() || undefined,
        driverLicenseExpiry: driverLicenseExpiry || undefined,
        driverEmail: driverEmail.trim() || undefined,
        driverPhone: driverPhone.trim() || undefined,
        loadType: loadType.trim() || undefined,
        equipmentType: equipmentType.trim() || undefined,
        licensePlate: licensePlate.trim() || undefined,
        licenseState: licenseState.trim() || undefined,
        status: EQUIPMENT_CONDITION_TO_API[equipmentCondition] || undefined,
        shipmentType: shipmentDirection,
        shipmentDirection,
        lookupSkipped: lookupSkipped || undefined,
        supervisorBypassReason: supervisorBypassReason.trim() || undefined,
        sealDiscrepancyNotes: outbound ? undefined : sealDiscrepancyNotes.trim() || undefined,
        sealVerified: outbound ? undefined : Boolean(sealVerified),
        performedByUserId:
          performedByUserId != null && performedByUserId !== ""
            ? Number(performedByUserId)
            : undefined,
      };

      // A walk-up has no Shipment yet, so it goes to the endpoint that creates one and checks
      // the trailer in against it inside a single server-side transaction (LGX-1104). Calling
      // POST /api/shipments here first and then /check-in would be two transactions, which is
      // exactly the half-created-record failure that design exists to close. shipmentNumber is
      // left out unless the guard typed one — the server generates it for the office.
      const walkUpPayload = manualEntry
        ? {
            ...checkInPayload,
            bolNumber: bolNumber.trim() || undefined,
            soNumber: soNumber.trim() || undefined,
            poNumber: poNumber.trim() || undefined,
            // Only set when the guard picked a carrier from the type-ahead. A hand-typed carrier
            // has no office code, and the check-in still goes through on carrierName alone — it
            // just cannot carry an address into the shipment record.
            carrierOfficeCode: carrierFields.officeCode.trim() || undefined,
            originName: originFields.name.trim() || undefined,
            originAddressLine1: originFields.addressLine1.trim() || undefined,
            originAddressLine2: originFields.addressLine2.trim() || undefined,
            originCity: originFields.city.trim() || undefined,
            originState: originFields.state.trim() || undefined,
            originZipCode: originFields.zipCode.trim() || undefined,
            originCountry: originFields.country.trim() || undefined,
            destinationName: destinationFields.name.trim() || undefined,
            destinationAddressLine1: destinationFields.addressLine1.trim() || undefined,
            destinationAddressLine2: destinationFields.addressLine2.trim() || undefined,
            destinationCity: destinationFields.city.trim() || undefined,
            destinationState: destinationFields.state.trim() || undefined,
            destinationZipCode: destinationFields.zipCode.trim() || undefined,
            destinationCountry: destinationFields.country.trim() || undefined,
          }
        : null;

      const useMultipart = Boolean(dlFile);
      const files = useMultipart
        ? { driverLicenseImage: dlFile, sealPhoto: sealImageFile ?? undefined }
        : null;
      const createdTrailer = manualEntry
        ? await checkInWalkUp(walkUpPayload, files)
        : await checkInTrailer(checkInPayload, files);

      // On a walk-up the shipment number is generated server-side, so the response is the only
      // place it exists — without this the new grid row would show a blank Shipment Number for
      // freight that does in fact have one now.
      const effectiveShipmentNumber =
        shipmentNo.trim() || createdTrailer?.shipmentNumber || appointment.shipmentNumber || '';

      const mergedAppt = {
        ...appointment,
        shipmentNumber: effectiveShipmentNumber || undefined,
        shipmentType: shipmentDirection,
        trailerId:
          createdTrailer?.id ??
          createdTrailer?.trailerId ??
          appointment.trailerId,
      };

      const gridRow = buildTrailerGridRowFromCheckIn({
        appointment: mergedAppt,
        trailerNumber: trailerNo || appointment?.trailerNumber,
        carrierName: carrier,
        locationLabel: locationForApi,
        shipmentNumberOverride: effectiveShipmentNumber,
        shipmentDirection,
      });
      const serverId =
        createdTrailer?.id ??
        createdTrailer?.trailerId ??
        createdTrailer?.data?.id;
      if (serverId != null) {
        gridRow.id = serverId;
      }

      recordPendingCheckInTrailerRow(gridRow);
      window.dispatchEvent(new CustomEvent("yard-trailer-check-in"));

      notify.success({
        title: "Checked in",
        message: "Trailer created and yard grid updated.",
      });
      onSubmitted?.();
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      notify.error({
        title: "Check-in failed",
        message:
          data?.message ||
          data?.content ||
          "The server could not complete check-in. Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // No padding on the scrolling element itself -- position: sticky computes its stuck offset
    // against this container's own padding edge, so a p-fluid-4 here left a gap above the
    // sticky header on scroll where content from the section below could show through
    // (matches TrailerDetailView.js's outer container, which never had this padding either).
    <div className="w-full h-full min-h-0 overflow-y-auto font-roboto">
      {/* Header — title on the left, actions on the right (matches TrailerDetailView.js / ShipmentDetailsView.js) */}
      <div className="sticky top-0 z-10 bg-white dark:bg-acs-dark-surface shadow-md flex items-center justify-between flex-wrap gap-fluid-2 px-fluid-4 py-fluid-2">
        <h2 className="text-fluid-lg font-bold text-gray-900 dark:text-acs-dark-text m-0">
          {manualEntry ? "New Check In (Walk-Up)" : `Appointment ${titleId}: Check In`}
        </h2>
        <div className="flex items-center gap-fluid-2 flex-wrap">
          <button type="button" onClick={onClose} className={toolbarSecondaryButtonClass}>
            Close
          </button>
          <button
            type="button"
            onClick={handleComplete}
            disabled={!canComplete}
            title={!canComplete && submitBlockedHint ? submitBlockedHint : undefined}
            className={toolbarPrimaryButtonClass}
          >
            {submitting ? "Submitting…" : "Complete Check In"}
          </button>
        </div>
      </div>

      {/* Everything below the sticky header lives in its own padded wrapper -- the header itself
          carries its own px/py above so it isn't pushed off the scrollport's true top edge. */}
      <div className="p-fluid-4">
      {/*
        Inline, not a toast: the guard needs this explanation to stay on screen while filling
        the form in, and it is the only thing telling them why every field is empty (LGX-1101).
      */}
      {manualEntry && (
        <p className="text-fluid-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-fluid-3 py-fluid-2 mb-fluid-3 dark:bg-blue-950/40 dark:border-blue-900 dark:text-blue-100">
          {manualSearchTerm
            ? `No appointment found for "${manualSearchTerm}". Fill in the details below to create a new check-in.`
            : "No appointment selected. Fill in the details below to create a new check-in."}
        </p>
      )}

      {appointment?._gateEligibility &&
        appointment._gateEligibility.eligible === false && (
          <p className="text-fluid-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-fluid-3 py-fluid-2 mb-fluid-3">
            {appointment._gateEligibility.message ||
              appointment._gateEligibility.reason ||
              "This appointment is not eligible for gate check-in. Supervisor override may be required."}
          </p>
        )}

      {/* Hidden file inputs — triggered via icon buttons attached to Seal Number / Driver License Number below */}
      <input
        ref={sealImageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          setSealImageFile(f || null);
          e.target.value = "";
        }}
      />
      <input
        ref={dlInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          setDlFile(f || null);
          touch("dlFile");
          e.target.value = "";
        }}
      />

      {/* Section 1 — Core Shipment Details (mostly pre-filled from the shipment) */}
      <div className={sectionCardClass}>
        <div className={sectionHeaderBarClass}>Core Shipment Details</div>
        <div className={sectionFieldGridClass}>
          {/* Row 1 */}
          <DisplayDetail
            label="Shipment Number"
            value={shipmentNo}
            isEditable
            onChange={(v) => setShipmentNo(v)}
          />
          {/* Editable on a walk-up only. There the Shipment does not exist yet, so these are the
              only chance to record the order numbers off the driver's paperwork. On a matched
              appointment they belong to a Shipment that already exists and is edited from the
              shipment screens — an editable box the gate cannot save would be worse than a
              read-only one. */}
          <DisplayDetail
            label="SO Number"
            value={soNumber}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setSoNumber(v) : undefined}
          />
          <DisplayDetail
            label="PO Number"
            value={poNumber}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setPoNumber(v) : undefined}
          />
          {/* Editable in create mode, where this is the paperwork identifier the new Shipment is
              keyed by and there is no appointment to read it from. Optional — a walk-up with no
              paperwork number still checks in. Stored as a BOL reference on the Shipment, which
              has no BOL column of its own (LGX-1103).
              On the matched path it is read-only and shown only when the shipment actually
              carries one, so a matched check-in gains a field rather than an empty box. */}
          {(manualEntry || bolNumber) && (
            <DisplayDetail
              label="BOL / Reference Number"
              value={bolNumber}
              isEditable={manualEntry}
              onChange={manualEntry ? (v) => setBolNumber(v) : undefined}
            />
          )}
          <FormField label="Movement Type" required>
            <select
              className={gridSelectClass}
              value={gateShipmentType}
              onChange={(e) => { setGateShipmentType(e.target.value); touch("gateShipmentType"); }}
              disabled={cancelled}
            >
              <option value="Inbound">Inbound</option>
              <option value="Outbound">Outbound</option>
            </select>
          </FormField>
        </div>

        <div className={sectionFieldGridClass}>
          <DisplayDetail
            label="Total Weight"
            value={[appointment?.totalWeight, appointment?.weightUnit].filter(Boolean).join(" ")}
          />
          <DisplayDetail
            label="Total Volume"
            value={[appointment?.totalVolume, appointment?.volumeUnit].filter(Boolean).join(" ")}
          />
          <DisplayDetail label="Number of Pallets" value={appointment?.numberOfPallets} />
        </div>

        {/* Origin / Destination are read-only reflections of the appointment on the matched path,
            but on a walk-up they are INPUTS — they become the two stops on the Shipment this
            check-in creates, so there is nothing to reflect until the guard types them. */}
        <div className={sectionFieldGridClass}>
          {manualEntry ? (
            <FacilityTypeahead
              label="Origin Name"
              value={originFields.name}
              onChange={(v) => setOriginField("name", v)}
              onPick={applyOriginFacility}
              officeCode={officeCodeForApi}
              disabled={cancelled}
              placeholder="Search saved facilities…"
            />
          ) : (
            <DisplayDetail label="Origin Name" value={appointment?.originName} />
          )}
          <DisplayDetail
            label="Origin Address Line 1"
            value={manualEntry ? originFields.addressLine1 : appointment?.originAddressLine1}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setOriginField("addressLine1", v) : undefined}
          />
          <DisplayDetail
            label="Origin Address Line 2"
            value={manualEntry ? originFields.addressLine2 : appointment?.originAddressLine2}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setOriginField("addressLine2", v) : undefined}
          />
          <DisplayDetail
            label="Origin City"
            value={manualEntry ? originFields.city : appointment?.originCity}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setOriginField("city", v) : undefined}
          />
          <DisplayDetail
            label="Origin State"
            value={manualEntry ? originFields.state : appointment?.originState}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setOriginField("state", v) : undefined}
          />
          <DisplayDetail
            label="Origin Zip Code"
            value={manualEntry ? originFields.zipCode : appointment?.originZip}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setOriginField("zipCode", v) : undefined}
          />
          <DisplayDetail
            label="Origin Country"
            value={manualEntry ? originFields.country : appointment?.originCountry}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setOriginField("country", v) : undefined}
          />
        </div>

        <div className={sectionFieldGridClass}>
          {manualEntry ? (
            <FacilityTypeahead
              label="Destination Name"
              value={destinationFields.name}
              onChange={(v) => setDestinationField("name", v)}
              onPick={applyDestinationFacility}
              officeCode={officeCodeForApi}
              disabled={cancelled}
              placeholder="Search saved facilities…"
            />
          ) : (
            <DisplayDetail label="Destination Name" value={appointment?.destinationName} />
          )}
          <DisplayDetail
            label="Destination Address Line 1"
            value={manualEntry ? destinationFields.addressLine1 : appointment?.destinationAddressLine1}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setDestinationField("addressLine1", v) : undefined}
          />
          <DisplayDetail
            label="Destination Address Line 2"
            value={manualEntry ? destinationFields.addressLine2 : appointment?.destinationAddressLine2}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setDestinationField("addressLine2", v) : undefined}
          />
          <DisplayDetail
            label="Destination City"
            value={manualEntry ? destinationFields.city : appointment?.destinationCity}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setDestinationField("city", v) : undefined}
          />
          <DisplayDetail
            label="Destination State"
            value={manualEntry ? destinationFields.state : appointment?.destinationState}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setDestinationField("state", v) : undefined}
          />
          <DisplayDetail
            label="Destination Zip Code"
            value={manualEntry ? destinationFields.zipCode : appointment?.destinationZip}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setDestinationField("zipCode", v) : undefined}
          />
          <DisplayDetail
            label="Destination Country"
            value={manualEntry ? destinationFields.country : appointment?.destinationCountry}
            isEditable={manualEntry}
            onChange={manualEntry ? (v) => setDestinationField("country", v) : undefined}
          />
        </div>

        <div className={sectionFieldGridClass}>
          <DisplayDetail label="Carrier Office Code" value={carrierFields.officeCode} />
          <CarrierTypeahead
            label="Carrier Name"
            value={carrier}
            onChange={handleCarrierNameChange}
            onPick={applyPickedCarrier}
            disabled={cancelled}
            placeholder="Search carriers…"
          />
          <DisplayDetail label="Carrier Address Line 1" value={carrierFields.addressLine1} />
          <DisplayDetail label="Carrier City" value={carrierFields.city} />
          <DisplayDetail label="Carrier State" value={carrierFields.state} />
          <DisplayDetail label="Carrier Zip Code" value={carrierFields.zipCode} />
        </div>
        {carrier.trim() === "" && show("carrier") && (
          <p className="px-fluid-3 pb-fluid-2 text-fluid-xs text-red-600 bg-[#F9FAFE] dark:bg-acs-dark-surface -mt-fluid-2">
            {show("carrier")}
          </p>
        )}
      </div>

      {/* Section 2 — Equipment */}
      <div className={sectionCardClass}>
        <div className={sectionHeaderBarClass}>Equipment</div>
        <div className={sectionFieldGridClass}>
          <DisplayDetail
            label="Equipment Type"
            value={equipmentType}
            isEditable
            onChange={(v) => setEquipmentType(v)}
          />
          <DisplayDetail
            label="Equipment Number"
            value={trailerNo}
            isEditable
            onChange={(v) => { setTrailerNo(v); touch("trailerNo"); }}
            highlighted={Boolean(errors.trailerNo)}
          />
          <DisplayDetail
            label="License Plate"
            value={licensePlate}
            isEditable
            onChange={(v) => setLicensePlate(v)}
          />
          <DisplayDetail
            label="License State"
            value={licenseState}
            isEditable
            onChange={(v) => setLicenseState(v.slice(0, 2).toUpperCase())}
          />
          <FormField label="Condition" required error={show("equipmentCondition")} highlighted={Boolean(errors.equipmentCondition)}>
            <select
              className={gridSelectClass}
              value={equipmentCondition}
              onChange={(e) => { setEquipmentCondition(e.target.value); touch("equipmentCondition"); }}
              onBlur={() => touch("equipmentCondition")}
              disabled={cancelled}
            >
              <option value="">Select</option>
              {EQUIPMENT_CONDITION_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </FormField>
          {/* Constrained to the two real load types rather than free text. Load Type is not a
              label — the backend branches on it for the gate-actual sync, and Hostler jobs are
              rejected outright for anything that is not exactly DROP_TRAILER, so a typo here
              silently costs the trailer its ability to be moved by a hostler later. */}
          <FormField label="Load Type" required error={show("loadType")}>
            <select
              className={gridSelectClass}
              value={loadType}
              onChange={(e) => { setLoadType(e.target.value); touch("loadType"); }}
              onBlur={() => touch("loadType")}
              disabled={cancelled}
            >
              <option value="">Select</option>
              {LOAD_TYPE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </FormField>
          <DisplayDetail
            label="Tare Weight"
            value={weight}
            isEditable
            inputType="text"
            onChange={(v) => handleWeightChange(v)}
          />
          <DisplayDetail
            label="Seal Number"
            value={sealNo}
            isEditable
            onChange={(v) => { setSealNo(v); touch("sealNo"); }}
            highlighted={Boolean(errors.sealNo)}
            icon={
              <span title={sealImageFile ? sealImageFile.name : "Attach seal photo"}>
                <Camera size={15} className={sealImageFile ? "text-acs-primary-navy" : "text-gray-400"} />
              </span>
            }
            onClick={() => sealImageInputRef.current?.click()}
          />
        </div>
        {(show("trailerNo") || show("equipmentCondition") || show("sealNo") || show("weight")) && (
          <div className="px-fluid-3 pb-fluid-2 bg-[#F9FAFE] dark:bg-acs-dark-surface -mt-fluid-2 space-y-fluid-0.5">
            {show("trailerNo") && <p className="text-fluid-xs text-red-600">{show("trailerNo")}</p>}
            {show("sealNo") && <p className="text-fluid-xs text-red-600">{show("sealNo")}</p>}
            {show("weight") && <p className="text-fluid-xs text-red-600">{show("weight")}</p>}
          </div>
        )}
      </div>

      {/* Section 3 — Driver Information */}
      <div className={sectionCardClass}>
        <div className={sectionHeaderBarClass}>Driver Information</div>
        <div className={sectionFieldGridClass}>
          <DisplayDetail
            label="Driver Name"
            value={driverName}
            isEditable
            onChange={(v) => { setDriverName(v); touch("driverName"); }}
            highlighted={Boolean(errors.driverName)}
          />
          <DisplayDetail
            label="Driver License Number"
            value={driverDlNumber}
            isEditable
            onChange={(v) => { setDriverDlNumber(v); touch("driverDlNumber"); }}
            highlighted={Boolean(errors.driverDlNumber)}
            icon={
              <span title={dlFile ? dlFile.name : "Scan / upload driver license"}>
                <Camera size={15} className={dlFile ? "text-acs-primary-navy" : "text-gray-400"} />
              </span>
            }
            onClick={() => dlInputRef.current?.click()}
          />
          <DisplayDetail
            label="Driver License State"
            value={driverLicenseState}
            isEditable
            onChange={(v) => setDriverLicenseState(v.slice(0, 2).toUpperCase())}
          />
          <DisplayDetail
            label="Expiry Date"
            value={driverLicenseExpiry}
            isEditable
            inputType="date"
            showCalendar
            onChange={(v) => setDriverLicenseExpiry(v)}
          />
          <DisplayDetail
            label="Driver Email"
            value={driverEmail}
            isEditable
            inputType="email"
            onChange={(v) => setDriverEmail(v)}
          />
          <DisplayDetail
            label="Driver Phone (with country code)"
            value={driverPhone}
            isEditable
            inputType="tel"
            onChange={(v) => setDriverPhone(v)}
          />
        </div>
        {(show("driverName") || show("driverDlNumber")) && (
          <div className="px-fluid-3 pb-fluid-2 bg-[#F9FAFE] dark:bg-acs-dark-surface -mt-fluid-2 space-y-fluid-0.5">
            {show("driverName") && <p className="text-fluid-xs text-red-600">{show("driverName")}</p>}
            {show("driverDlNumber") && <p className="text-fluid-xs text-red-600">{show("driverDlNumber")}</p>}
          </div>
        )}
      </div>

      {/* Section 4 — Yard Information: dictates whether the driver is sent to a dock or a parking bay */}
      <div className={sectionCardClass}>
        <div className={sectionHeaderBarClass}>Yard Information</div>

        {/* Row 1 — Location + Parking Bay path (Section/Parking Bay hang off the plant directly) */}
        <div className={`${sectionFieldGridClass} pb-fluid-1`}>
          {manualEntry && (plantOptions.length > 0 || plantOptionsLoading) ? (
            /* Walk-up: there is no appointment to seed a plant from, so this is a picker instead
               of the free-text field the matched path edits.
               <p>
               This sets the PLANT (plantLocationId), NOT warehouseName. warehouseName is the key
               yard settings are stored under, and Yard Settings saves sections against the
               WAREHOUSE child's display name (yardsettingstab.js) — putting a plant name in it
               404s /api/yard-settings, which fetchYardSettings swallows into an empty section
               list, silently disabling Section and Parking Bay with no error shown. So this
               field feeds the cascade the same way the matched path's appointment does: pick the
               plant here, and the "Warehouse" dropdowns beside/below populate from its children
               and set warehouseName themselves.
               <p>
               Falls back to the free-text DisplayDetail when the list is empty (load failed, or
               the office has no POINT locations), so a walk-up is never blocked by this. */
            <FormField
              label="Location"
              className="col-span-6 sm:col-span-2"
              required
              error={plantLocationId ? "" : show("warehouseName")}
              highlighted={Boolean(!plantLocationId && errors.warehouseName)}
            >
              <select
                className={gridSelectClass}
                value={plantOptions.some((loc) => String(loc.id) === String(plantLocationId))
                  ? String(plantLocationId)
                  : ""}
                onChange={(e) => {
                  const picked = plantOptions.find((loc) => String(loc.id) === e.target.value);
                  // From here on the guard has stated the facility outright - the warehouse-name
                  // resolver must stop deriving it. Set before the state updates so the effect
                  // this pick triggers already sees it.
                  plantPickedByGuardRef.current = Boolean(picked);
                  setPlantLocationId(picked?.id ?? null);
                  setPlantLocationName(picked?.name ?? "");
                  // Changing the facility invalidates every downstream selection — the
                  // sections/bays hang off the plant and the docks off its warehouse children.
                  setWarehouseName("");
                  setWarehouseLocationRef("");
                  setSectionName("");
                  setBayName("");
                  setDockName("");
                  setDockWarehouseName("");
                  setDockWarehouseRef("");
                  touch("warehouseName");
                  touch("yardLayout");
                }}
                onBlur={() => { touch("warehouseName"); touch("yardLayout"); }}
                disabled={cancelled || plantOptionsLoading}
              >
                <option value="">
                  {plantOptionsLoading ? "Loading locations…" : "Select location"}
                </option>
                {plantOptions.map((loc) => (
                  <option key={String(loc.id ?? loc.ref ?? loc.name)} value={String(loc.id)}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </FormField>
          ) : (
            <DisplayDetail
              label="Location"
              // Shows the resolved PLANT name (e.g. "Diamond Pet Foods - Lathrop"), not the raw
              // warehouseName -- which can itself already be a specific child warehouse (e.g.
              // "Roth Warehouse") when that's what the appointment names directly, and would
              // otherwise show up here duplicating the "Warehouse" field beside it.
              value={plantLocationName || warehouseName}
              className="col-span-6 sm:col-span-2"
              isEditable
              onChange={(v) => { setWarehouseName(v); touch("warehouseName"); touch("yardLayout"); }}
              highlighted={Boolean(errors.warehouseName)}
            />
          )}
          <FormField
            label="Warehouse"
          >
            {/* Same sibling-warehouse list the Dock path already offers below — picking one here
                places the trailer in THAT warehouse's bays, which can differ from the appointment's
                own warehouse (e.g. an overflow parking bay in a different warehouse under the same
                plant). Selecting here just re-seeds warehouseName, the same field "Location" above
                edits as free text — this is a discoverable dropdown alternative to it, not a
                separate mechanism. */}
            <select
              className={gridSelectClass}
              value={dockWarehouseOptions.some((wh) => wh.name === warehouseName) ? warehouseName : ""}
              onChange={(e) => {
                if (!e.target.value) return;
                setWarehouseName(e.target.value);
                setSectionName("");
                setBayName("");
                touch("warehouseName");
                touch("yardLayout");
              }}
              disabled={cancelled || dockWarehouseLoading || !dockWarehouseOptions.length}
            >
              <option value="">
                {dockWarehouseLoading ? "Loading warehouses…" : dockWarehouseOptions.length ? "Select warehouse for bay" : "—"}
              </option>
              {dockWarehouseOptions.map((wh) => (
                <option key={String(wh.id ?? wh.name)} value={wh.name}>{wh.name}</option>
              ))}
            </select>
          </FormField>
          <FormField
            label="Section"
            required={!usesDockPath && hasBayLayout}
            error={show("sectionName") || show("sectionLayout")}
            highlighted={Boolean(errors.sectionName || errors.sectionLayout)}
          >
            <select
              className={gridSelectClass}
              value={sectionName}
              onChange={(e) => {
                setSectionName(e.target.value);
                setBayName("");
                setDockName("");
                setDockWarehouseName("");
                setDockWarehouseRef("");
                touch("sectionName");
                touch("sectionLayout");
                touch("yardLocation");
                touch("dockName");
              }}
              onBlur={() => { touch("sectionName"); touch("sectionLayout"); touch("yardLocation"); }}
              disabled={cancelled || yardLoading || usesDockPath || !yardSections.length}
            >
              <option value="">Select section</option>
              {yardSections.map((s) => (
                <option key={String(s.sectionId ?? s.name)} value={String(s.name ?? "")}>
                  {s.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField
            label="Parking Bay"
            required={usesBayPath && needsBay}
            error={show("bayName") || show("bayLocationId")}
            highlighted={Boolean(errors.bayName || errors.bayLocationId)}
          >
            <select
              className={gridSelectClass}
              value={bayName}
              onChange={(e) => {
                setBayName(e.target.value);
                if (e.target.value) { setDockName(""); setDockWarehouseName(""); setDockWarehouseRef(""); }
                touch("bayName");
                touch("yardLocation");
                touch("dockName");
              }}
              onBlur={() => { touch("bayName"); touch("yardLocation"); }}
              disabled={cancelled || usesDockPath || !sectionName || !bayOptions.length}
            >
              <option value="">{bayOptions.length ? "Select bay" : "—"}</option>
              {bayOptions.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </FormField>
        </div>

        {/* Row 2 — Dock path: pick a Warehouse (a child of the plant, Appointment module's own
            hierarchy), then a Dock scoped to that specific warehouse. */}
        <div className={`${sectionFieldGridClass} pt-0`}>
          <FormField label="Warehouse">
            <select
              className={gridSelectClass}
              value={dockWarehouseName}
              onChange={(e) => {
                setDockWarehouseName(e.target.value);
                setDockWarehouseRef(
                  dockWarehouseOptions.find((wh) => wh.name === e.target.value)?.ref || ""
                );
                setDockName("");
                if (e.target.value) { setSectionName(""); setBayName(""); }
                touch("dockName");
                touch("yardLocation");
                touch("sectionName");
                touch("bayName");
              }}
              disabled={cancelled || usesBayPath || dockWarehouseLoading || !dockWarehouseOptions.length}
            >
              <option value="">{dockWarehouseOptions.length ? "Select warehouse" : "—"}</option>
              {dockWarehouseOptions.map((wh) => (
                <option key={String(wh.id ?? wh.name)} value={wh.name}>{wh.name}</option>
              ))}
            </select>
          </FormField>
          <FormField
            label="Dock"
            required={usesDockPath && warehouseDockOptions.length > 0}
            error={show("dockName") || show("dockLocationId")}
            highlighted={Boolean(errors.dockName || errors.dockLocationId)}
          >
            <select
              className={gridSelectClass}
              value={dockName}
              onChange={(e) => {
                setDockName(e.target.value);
                touch("dockName");
                touch("yardLocation");
              }}
              onBlur={() => { touch("dockName"); touch("yardLocation"); }}
              disabled={cancelled || !dockWarehouseName || warehouseDocksLoading || !warehouseDockOptions.length}
            >
              <option value="">{warehouseDockOptions.length ? "Select dock" : "—"}</option>
              {warehouseDockOptions.map((dock) => (
                <option key={String(dock.id ?? dock.name)} value={dock.name}>{dock.name}</option>
              ))}
            </select>
          </FormField>
        </div>

        <div className="px-fluid-3 pb-fluid-3 bg-[#F9FAFE] dark:bg-acs-dark-surface">
          {yardLoading && <p className="text-fluid-xs text-gray-500 mb-fluid-0.5">Loading yard layout…</p>}
          {locationsLoading && <p className="text-fluid-xs text-gray-500 mb-fluid-0.5">Loading server bay/dock inventory…</p>}
          {dockWarehouseLoading && <p className="text-fluid-xs text-gray-500 mb-fluid-0.5">Loading warehouses…</p>}
          {warehouseDocksLoading && <p className="text-fluid-xs text-gray-500 mb-fluid-0.5">Loading docks…</p>}
          <ErrorHint message={show("warehouseName")} />
          <ErrorHint message={show("locationsApi")} />
          <p className="text-fluid-sm font-semibold text-red-600 dark:text-red-400 mt-fluid-1">
            Select section and parking bay (top row), or select a warehouse and dock (bottom row) — this determines whether the driver is sent to a gate/dock or a parking bay.
          </p>
        </div>
      </div>

      {/* Section 5 — Seal & Supervisor Note */}
      <div className={sectionCardClass}>
        <div className={sectionHeaderBarClass}>Seal & Supervisor Note</div>
        <div className={`${sectionContentClass} space-y-3`}>
            {!outbound && (
              <>
                <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-acs-dark-text">
                  <input
                    type="checkbox"
                    checked={sealVerified}
                    onChange={(e) => setSealVerified(e.target.checked)}
                    disabled={cancelled}
                  />
                  Seal verified (matches expected)
                </label>
                <div>
                  <label className={labelClass}>Seal discrepancy notes</label>
                  <textarea
                    className={`${baseInput} min-h-[72px]`}
                    value={sealDiscrepancyNotes}
                    onChange={(e) => setSealDiscrepancyNotes(e.target.value)}
                    disabled={cancelled}
                    placeholder="If seal does not match, describe here."
                  />
                </div>
              </>
            )}
            <div>
              <label className={labelClass}>Supervisor bypass reason</label>
              <textarea
                className={`${baseInput} min-h-[60px]`}
                value={supervisorBypassReason}
                onChange={(e) => setSupervisorBypassReason(e.target.value)}
                disabled={cancelled}
                placeholder="Optional — recorded when override rules apply."
              />
            </div>
            <label
              className={`flex flex-col gap-1 text-sm ${
                canSkipGateLookup
                  ? "text-gray-800 dark:text-acs-dark-text"
                  : "text-gray-500 dark:text-acs-dark-subtext"
              }`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={lookupSkipped}
                  onChange={(e) => setLookupSkipped(e.target.checked)}
                  disabled={cancelled || !canSkipGateLookup}
                />
                Skip gate eligibility lookup (requires YMS:OVERRIDE_POST)
              </span>
              {!canSkipGateLookup ? (
                <span className="text-xs font-normal pl-6">
                  Your account does not have supervisor override for gate checks.
                </span>
              ) : null}
            </label>
        </div>
      </div>
      </div>
    </div>
  );
}
